import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';

/**
 * SSE(Server-Sent Events) 기능을 제공하는 composable
 * @param {Object} options - SSE 설정 옵션
 * @param {boolean} options.autoConnect - 컴포넌트 마운트 시 자동 연결 여부 (기본값: true)
 * @param {boolean} options.autoDisconnect - 컴포넌트 언마운트 시 자동 연결 해제 여부 (기본값: true)
 * @param {boolean} options.reconnectOnTokenChange - 토큰 변경 시 재연결 여부 (기본값: true)
 */
export function useSSE(options = {}) {
    const {
        autoConnect = true,
        autoDisconnect = true,
        reconnectOnTokenChange = true
    } = options;

    const store = useStore();
    
    // SSE 상태
    const isConnected = computed(() => store.getters['sse/isConnected']);
    const isConnecting = computed(() => store.getters['sse/isConnecting']);
    const connectionInfo = computed(() => store.getters['sse/connectionInfo']);
    const error = computed(() => store.getters['sse/error']);
    
    // 메시지 데이터
    const messages = computed(() => store.getters['sse/messages']);
    const notifications = computed(() => store.getters['sse/notifications']);
    const systemAlerts = computed(() => store.getters['sse/systemAlerts']);
    const realTimeData = computed(() => store.getters['sse/realTimeData']);
    const unreadNotificationCount = computed(() => store.getters['sse/unreadNotificationCount']);
    
    // 토큰 상태 감시
    const token = computed(() => store.getters['systemInfo/getToken']);
    
    // SSE 연결 함수들
    const connect = () => {
        console.log('🔗 useSSE: Connecting...');
        store.dispatch('sse/connect');
    };
    
    const disconnect = () => {
        console.log('🔌 useSSE: Disconnecting...');
        store.dispatch('sse/disconnect');
    };
    
    const reconnect = () => {
        console.log('🔄 useSSE: Reconnecting...');
        store.dispatch('sse/reconnect');
    };
    
    // 연결 상태 정보
    const lastHeartbeat = computed(() => store.state.sse?.connectionInfo?.lastHeartbeat);
    const reconnectAttempts = computed(() => store.state.sse?.reconnectAttempts || 0);
    const maxReconnectAttempts = computed(() => store.state.sse?.maxReconnectAttempts || 5);
    
    // 연결 건강성 체크
    const isHealthy = computed(() => {
        if (!isConnected.value) return false;
        
        const lastHB = lastHeartbeat.value;
        if (!lastHB) return true; // 아직 heartbeat이 없으면 연결된 것으로 간주
        
        const now = new Date();
        const timeSinceLastHeartbeat = now - new Date(lastHB);
        return timeSinceLastHeartbeat < 60000; // 1분 이내면 건강함
    });
    
    // 알림 관리 함수들
    const markNotificationRead = (notificationId) => {
        store.dispatch('sse/markNotificationRead', notificationId);
    };
    
    const markAllNotificationsRead = () => {
        store.dispatch('sse/markAllNotificationsRead');
    };

    const deleteReadNotifications = () => {
        store.dispatch('sse/deleteReadNotifications');
    };
    
    const clearMessages = () => {
        store.dispatch('sse/clearMessages');
    };
    
    const clearNotifications = () => {
        store.dispatch('sse/clearNotifications');
    };
    
    // 특정 키의 실시간 데이터 가져오기
    const getRealTimeData = (key) => {
        return computed(() => realTimeData.value[key]);
    };
    
    // 최신 알림 가져오기
    const getLatestNotifications = (count = 5) => {
        return computed(() => notifications.value.slice(0, count));
    };
    
    // 읽지 않은 알림만 가져오기
    const getUnreadNotifications = () => {
        return computed(() => notifications.value.filter(notification => !notification.read));
    };
    
    // 특정 타입의 시스템 알림 가져오기
    const getSystemAlertsByType = (type) => {
        return computed(() => systemAlerts.value.filter(alert => alert.type === type));
    };
    
    // 브라우저 알림 권한 요청
    const requestNotificationPermission = async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            console.log('🔔 Notification permission:', permission);
            return permission;
        }
        return Notification.permission;
    };
    
    // 테스트용 알림 시뮬레이션
    const simulateNotification = (type = 'notification') => {
        console.log('🧪 Simulating notification:', type);
        
        const testData = {
            notification: {
                eventSeq: Date.now(),
                alarmNm: '테스트 알림',
                alarmDesc: '{0}님이 새 메시지를 보냈습니다.',
                eventCont: '이것은 테스트 알림입니다. SSE가 정상적으로 작동하고 있습니다.',
                userNm: '테스트 사용자',
                alarmType: 'info',
                creDat: new Date().toISOString(),
                readYn: 'N'
            },
            system: {
                id: Date.now(),
                severity: 'warning',
                message: '시스템 점검이 예정되어 있습니다.',
                type: 'system'
            },
            update: {
                key: 'test-data',
                type: 'update',
                message: '데이터가 업데이트되었습니다.',
                showNotification: true,
                data: { count: Math.floor(Math.random() * 100) }
            }
        };
        
        const data = testData[type] || testData.notification;
        
        switch (type) {
            case 'notification':
                store.dispatch('sse/handleNewNotification', data);
                break;
            case 'system':
                store.dispatch('sse/handleSystemMessage', data);
                break;
            case 'update':
                store.dispatch('sse/handleDataUpdate', data);
                break;
            default:
                store.dispatch('sse/handleNewNotification', data);
        }
    };
    
    // 알림 설정 상태 (로컬 스토리지 기반)
    const notificationSettings = computed(() => {
        try {
            const saved = localStorage.getItem('sse-notification-settings');
            return saved ? JSON.parse(saved) : {
                enabled: true,
                sound: true,
                browser: true,
                toast: true
            };
        } catch {
            return {
                enabled: true,
                sound: true,
                browser: true,
                toast: true
            };
        }
    });
    
    // 알림 설정 업데이트
    const updateNotificationSettings = (settings) => {
        try {
            localStorage.setItem('sse-notification-settings', JSON.stringify(settings));
            console.log('🔧 Notification settings updated:', settings);
        } catch (error) {
            console.error('❌ Failed to save notification settings:', error);
        }
    };
    
    // 토큰 변경 감시 및 재연결
    if (reconnectOnTokenChange) {
        watch(token, (newToken, oldToken) => {
            if (newToken && newToken !== oldToken && isConnected.value) {
                console.log('🔑 Token changed, reconnecting SSE...');
                disconnect();
                setTimeout(() => {
                    connect();
                }, 1000);
            }
        });
    }
    
    // 라이프사이클 관리
    if (autoConnect) {
        onMounted(() => {
            console.log('📱 Component mounted, connecting SSE...');
            connect();
        });
    }
    
    if (autoDisconnect) {
        onUnmounted(() => {
            console.log('📱 Component unmounted, disconnecting SSE...');
            disconnect();
        });
    }
    
    return {
        // 상태
        isConnected,
        isConnecting,
        connectionInfo,
        error,
        
        // 데이터
        messages,
        notifications,
        systemAlerts,
        realTimeData,
        unreadNotificationCount,
        
        // 연결 관리
        connect,
        disconnect,
        reconnect,
        
        // 알림 관리
        markNotificationRead,
        markAllNotificationsRead,
        clearMessages,
        clearNotifications,
        requestNotificationPermission,
        deleteReadNotifications,
        
        // 유틸리티
        getRealTimeData,
        getLatestNotifications,
        getUnreadNotifications,
        getSystemAlertsByType,
        
        // 연결 상태 정보
        lastHeartbeat,
        reconnectAttempts,
        maxReconnectAttempts,
        isHealthy,
        
        // 새로 추가된 기능들
        simulateNotification,
        notificationSettings,
        updateNotificationSettings
    };
}

/**
 * 단순한 SSE 연결만 필요한 경우 사용하는 가벼운 composable
 */
export function useSSEConnection() {
    const store = useStore();
    
    return {
        isConnected: computed(() => store.getters['sse/isConnected']),
        connect: () => store.dispatch('sse/connect'),
        disconnect: () => store.dispatch('sse/disconnect')
    };
}

/**
 * 알림 기능에 특화된 composable
 */
export function useSSENotifications() {
    const store = useStore();
    
    const notifications = computed(() => store.getters['sse/notifications']);
    const unreadCount = computed(() => store.getters['sse/unreadNotificationCount']);
    
    const markRead = (id) => store.dispatch('sse/markNotificationRead', id);
    const markAllRead = () => store.dispatch('sse/markAllNotificationsRead');
    const clear = () => store.dispatch('sse/clearNotifications');
    const deleteRead = () => store.dispatch('sse/deleteReadNotifications');
    
    return {
        notifications,
        unreadCount,
        markRead,
        markAllRead,
        clear,
        deleteRead
    };
}

/**
 * 실시간 데이터에 특화된 composable
 */
export function useSSERealTimeData(dataKey) {
    const store = useStore();
    
    const data = computed(() => {
        const realTimeData = store.getters['sse/realTimeData'];
        return realTimeData[dataKey] || null;
    });
    
    const lastUpdated = computed(() => data.value?.lastUpdated || null);
    const isStale = computed(() => {
        if (!lastUpdated.value) return true;
        const now = new Date();
        const diff = now - lastUpdated.value;
        return diff > 30000; // 30초 이상 지나면 stale로 간주
    });
    
    return {
        data,
        lastUpdated,
        isStale
    };
} 